'use client';

import { useState } from 'react';

import { Column, Grid } from '@carbon/react';

import { FilterGroup, type FilterGroupLabel, FilterGroupObserver } from '@/components/FilterGroup';
import { SectionHeaderWithDescription } from '@/components/SectionHeaderWithDescription';
import {
  EVENT_AUDIENCE,
  EVENT_FORMATS,
  type FallFestQuantumEventWithImage,
} from '@/types/FallFestEvents';

import { FallFestEventsGrid } from './FallFestEventsGrid';

interface Props {
  upcomingEvents: FallFestQuantumEventWithImage[];
  pastEvents: FallFestQuantumEventWithImage[];
}

export function FallFestEventsSection({ upcomingEvents, pastEvents }: Props) {
  const [filter, setFilter] = useState<FilterGroupLabel[]>([]);

  const filterObserver = new FilterGroupObserver();

  const availableTags = [...upcomingEvents, ...pastEvents].reduce((availableTags, event) => {
    for (const tag of event.tags) {
      availableTags.add(tag);
    }

    return availableTags;
  }, new Set<string>());

  const eventFormatTags: FilterGroupLabel[] = mapAndSortTags(EVENT_FORMATS, availableTags);
  const eventAudienceTags: FilterGroupLabel[] = mapAndSortTags(EVENT_AUDIENCE, availableTags);
  const filterGroups = [
    { title: 'Event format', labels: eventFormatTags },
    { title: 'Event audience', labels: eventAudienceTags },
  ];

  const filteredUpcomingEvents = upcomingEvents.filter(
    (event) => !filter || filter.length === 0 || event.tags.includes(filter[0].text)
  );
  const filteredPastEvents = pastEvents.filter(
    (event) => !filter || filter.length === 0 || event.tags.includes(filter[0].text)
  );

  const eventsTitle = 'Fall Fest events';
  const eventsDescription = [
    {
      copy: (
        <>
          Events are led by some of our most dedicated student leaders of today. With these events,
          students are given the opportunity to put their leadership skills to the test and host
          their very own campus quantum computing event with the goal to grow the quantum community
          using resources and guidance from IBM®.
          <br />
          <br />
          Please note for in-person events you must be local or able to travel to the event
          location.
        </>
      ),
    },
  ];

  return (
    <>
      <Grid className="mb-64">
        <Column sm="100%" lg={16}>
          <SectionHeaderWithDescription title={eventsTitle} descriptions={eventsDescription} />
        </Column>
      </Grid>
      <Grid>
        <Column sm={4} md={8} lg={3}>
          <div>
            <FilterGroup
              groups={filterGroups}
              multiselect={false}
              onFilter={setFilter}
              filterGroupObserver={filterObserver}
            />
          </div>
        </Column>
        <Column sm={0} md={0} lg={1}></Column>
        <Column sm={4} md={8} lg={12}>
          <FallFestEventsGrid
            upcomingEvents={filteredUpcomingEvents}
            pastEvents={filteredPastEvents}
          />
        </Column>
      </Grid>
    </>
  );
}

function mapAndSortTags(tags: string[], originList: Set<string>): FilterGroupLabel[] {
  return tags.map((type) => ({
    text: type,
    disabled: !originList.has(type),
  }));
}
